import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';

import { AddressModel } from '@common-lib';

import { PortalTheme } from '../../theme';
import { SiteUnitCardImageDefault } from '../site-unit-card-image-default.component';

/**
 * This is very much a hack to allow most of the card to be clickable without
 * causing button in button issues. This unfortunately causes spotty
 * clickability since between some elements there is no clickable area. This
 * is a trade-off. Best would be if designer would listen to us instead and
 * understand that it is a bad design choice to have the entire card clickable
 * and at the same time have buttons inside the card.
 *
 * All clickable wrappers should have tabIndex set to -1 except for 1. That
 * allows proper tabbing behavior where you can tab to a card, then its action,
 * and then last tab to next available HTML element.
 */
const ClickableWrapper = styled(Button)({
  padding: 0,
  margin: 0,
  background: 'transparent',
  color: 'inherit',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  textTransform: 'none',
  wordWrap: 'inherit',
  textAlign: 'left',
  justifyContent: 'start',
  width: '100%',
});

export interface SiteUnitCardProps {
  displayName: string;
  description: string | undefined;
  headerImageURL?: string;
  headerImageAltText?: string;
  isLoggedIn: boolean;
  descriptionVisibility?: 'show' | 'hide';
  addressVisibility?: 'streetCity' | 'hide';
  onClick: () => void;
  favoriteComponent?: () => React.ReactNode;
  address?: AddressModel | undefined;
  ariaLabel?: string;
}

export const SiteUnitCard = (props: SiteUnitCardProps) => {
  const theme = useTheme<PortalTheme>();

  const formattedAddress = useMemo(() => {
    return AddressModel.formatAddress(props.address);
  }, [props?.address]);

  const showDescription =
    props.description && props.descriptionVisibility === 'show';
  const showAddress =
    formattedAddress && props.addressVisibility === 'streetCity';

  return (
    <Card
      title={props.displayName}
      sx={{
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        boxShadow: theme.boxShadow,

        '&:focus-within': {
          /*
           * The card is not a focusable component but is still clickable.
           * At the moment it doesn't need to be since there is a button
           * that perform the same action as clicking the card. But when
           * focusing a button in the card it should be more clear that we
           * have focused something in the card.
           *
           * An alternative solution would be to use a standard html button
           * instead and style it same as current card. But that would
           * circumvent theming which is needed in the future.
           */
          background: theme.palette.primary.dark,
        },
      }}
      aria-label={props.ariaLabel}
    >
      <Stack
        height="100%"
        justifyContent={'space-between'}
        flexDirection={'column'}
      >
        <ClickableWrapper tabIndex={-1} onClick={props.onClick}>
          {props.headerImageURL && (
            <CardMedia
              sx={{ aspectRatio: '3/2', objectFit: 'cover' }}
              role={props.headerImageAltText ? 'img' : 'presentation'}
              component="img"
              alt={props.headerImageAltText}
              image={props.headerImageURL}
              loading="lazy"
            />
          )}
          {!props.headerImageURL && <SiteUnitCardImageDefault />}
        </ClickableWrapper>

        <CardContent
          sx={{
            backgroundColor: theme.palette.background.paper,
            height: 'fit-content',
            p: 0,
            '* > h6': {
              p: 1.5,
              pb: 0,
            },
            '* > p': {
              px: 1.5,
            },
            ':last-child': { pb: 0 },
          }}
        >
          <Stack height="100%">
            <Stack direction="row" justifyContent="space-between">
              <ClickableWrapper
                disableFocusRipple
                onClick={props.onClick}
                sx={{
                  pb: !showDescription && !showAddress ? 1.5 : undefined,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    overflow: 'hidden',
                  }}
                >
                  {props.displayName}
                </Typography>
              </ClickableWrapper>
              {props.isLoggedIn && props.favoriteComponent && (
                <Box
                  sx={{
                    pt: 0.75,
                    pr: 0.5,
                  }}
                >
                  {props.favoriteComponent()}
                </Box>
              )}
            </Stack>

            {showDescription && (
              <ClickableWrapper tabIndex={-1} onClick={props.onClick}>
                <Typography
                  variant="subtitle2"
                  component="p"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    pt: 0.5,
                    pb: !showAddress ? 1.5 : undefined,
                  }}
                >
                  {props.description}
                </Typography>
              </ClickableWrapper>
            )}
            {showAddress && (
              <ClickableWrapper tabIndex={-1} onClick={props.onClick}>
                <Typography
                  variant="subtitle2"
                  component="p"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineClamp: '2',
                    WebkitLineClamp: '2',
                    pt: 0.5,
                    pb: 1.5,
                  }}
                >
                  {formattedAddress}
                </Typography>
              </ClickableWrapper>
            )}
          </Stack>
        </CardContent>
      </Stack>
    </Card>
  );
};
